import { useState, useCallback } from 'react';
import { Dropzone, type FileRejection } from '@mantine/dropzone';
import { IconUpload, IconFile, IconX, IconLoader2 } from '@tabler/icons-react';
import { ACCEPTED_MIME_TYPES, MAX_FILE_SIZE } from '../../types/medicalRecords';

interface RecordDropzoneProps {
  onUpload: (files: File[]) => Promise<boolean>;
  disabled?: boolean;
}

export function RecordDropzone({ onUpload, disabled = false }: RecordDropzoneProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadCount, setUploadCount] = useState(0);

  const handleDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setIsUploading(true);
      setError(null);
      setUploadCount(files.length);

      try {
        const success = await onUpload(files);
        if (!success) {
          setError('Failed to upload some files');
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError('Failed to upload files');
      } finally {
        setIsUploading(false);
        setUploadCount(0);
      }
    },
    [onUpload]
  );

  const handleReject = useCallback((rejections: FileRejection[]) => {
    if (rejections.length > 0) {
      const firstError = rejections[0].errors[0]?.message;
      if (firstError?.includes('larger than')) {
        setError('File too large. Maximum size is 25MB.');
      } else if (firstError?.includes('type')) {
        setError('Invalid file type. Please upload PDF or image files.');
      } else {
        setError(firstError || 'Invalid file');
      }
    }
  }, []);

  return (
    <div className="space-y-2">
      <Dropzone
        onDrop={handleDrop}
        onReject={handleReject}
        accept={ACCEPTED_MIME_TYPES}
        maxSize={MAX_FILE_SIZE}
        disabled={disabled || isUploading}
        loading={isUploading}
        multiple
        styles={{
          root: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderColor: error ? 'rgba(239, 68, 68, 0.5)' : 'rgba(75, 85, 99, 0.5)',
            borderWidth: '2px',
            borderStyle: 'dashed',
            borderRadius: '0.75rem',
            padding: '2.5rem 2rem',
            cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(0, 214, 245, 0.5)',
              backgroundColor: 'rgba(0, 214, 245, 0.05)',
            },
            '&[data-accept]': {
              borderColor: 'rgba(34, 197, 94, 0.5)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
            },
            '&[data-reject]': {
              borderColor: 'rgba(239, 68, 68, 0.5)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
            },
          },
        }}
      >
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <Dropzone.Accept>
            <IconUpload size={48} className="text-green-400" stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={48} className="text-red-400" stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            {isUploading ? (
              <IconLoader2 size={48} className="text-cyan-400 animate-spin" stroke={1.5} />
            ) : (
              <IconFile size={48} className="text-gray-500" stroke={1.5} />
            )}
          </Dropzone.Idle>

          <div>
            <p className="text-base text-gray-300">
              {isUploading
                ? `Uploading ${uploadCount} file${uploadCount !== 1 ? 's' : ''}...`
                : 'Drop files here or click to upload'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              PDF or images (PNG, JPG, HEIC) up to 25MB each
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              You can upload multiple files at once
            </p>
          </div>
        </div>
      </Dropzone>

      {error && (
        <p className="text-sm text-red-400 mt-2">{error}</p>
      )}
    </div>
  );
}
